import React, { Component, createContext } from 'react';

const CartContext = createContext({
  cart: [],
  cartCount: 0,
  addToCart: () => {},
  removeFromCart: () => {},
  updateCartQuantity: () => {},
  updateCartItem: () => {},
  replaceCart: () => {},
  clearCart: () => {},
  isInCart: () => false,
});

class CartProvider extends Component {
  constructor(props) {
    super(props);
    var saved = [];
    try {
      var raw = localStorage.getItem('primal_cart');
      if (raw) saved = JSON.parse(raw);
    } catch (e) { /* ignore */ }
    // Track current user for per-user cart persistence
    this._userId = null;
    try {
      var profileStr = localStorage.getItem('primal_user_profile');
      if (profileStr) this._userId = JSON.parse(profileStr)._id;
    } catch (e) { /* ignore */ }
    this.state = {
      cart: Array.isArray(saved) ? saved : [],
    };
    this.addToCart = this.addToCart.bind(this);
    this.removeFromCart = this.removeFromCart.bind(this);
    this.updateCartQuantity = this.updateCartQuantity.bind(this);
    this.updateCartItem = this.updateCartItem.bind(this);
    this.replaceCart = this.replaceCart.bind(this);
    this.clearCart = this.clearCart.bind(this);
    this.isInCart = this.isInCart.bind(this);
    this.handleAuthStateChanged = this.handleAuthStateChanged.bind(this);
    this.handleCartCleared = this.handleCartCleared.bind(this);
  }

  componentDidMount() {
    window.addEventListener('authStateChanged', this.handleAuthStateChanged);
    window.addEventListener('cartCleared', this.handleCartCleared);
  }

  componentWillUnmount() {
    window.removeEventListener('authStateChanged', this.handleAuthStateChanged);
    window.removeEventListener('cartCleared', this.handleCartCleared);
  }

  handleCartCleared() {
    this.setState({ cart: [] });
  }

  handleAuthStateChanged() {
    try {
      var profileStr = localStorage.getItem('primal_user_profile');
      if (!profileStr) {
        // Logged out: save cart under user-specific key before clearing
        if (this._userId && this.state.cart.length > 0) {
          localStorage.setItem('primal_cart_' + this._userId, JSON.stringify(this.state.cart));
        }
        this._userId = null;
        this.setState({ cart: [] });
        localStorage.removeItem('primal_cart');
      } else {
        // Logged in: restore cart from user-specific key if available
        var userId = JSON.parse(profileStr)._id;
        this._userId = userId;
        var userRaw = localStorage.getItem('primal_cart_' + userId);
        var activeRaw = localStorage.getItem('primal_cart');
        // Prefer user-specific saved cart, fall back to active cart
        var raw = userRaw || activeRaw;
        var saved = raw ? JSON.parse(raw) : [];
        var cart = Array.isArray(saved) ? saved : [];
        localStorage.setItem('primal_cart', JSON.stringify(cart));
        if (userRaw) localStorage.removeItem('primal_cart_' + userId);
        this.setState({ cart: cart });
      }
    } catch (e) { /* ignore */ }
  }

  addToCart(item) {
    if (!item || !item.postId) return;
    var qty = item.quantity || 1;
    // If already in cart, increment quantity up to available
    if (this.isInCart(item.postId)) {
      this.setState(function(prev) {
        var newCart = prev.cart.map(function(ci) {
          if (ci.postId === item.postId) {
            var newQty = (ci.quantity || 1) + qty;
            var maxQty = item.availableQuantity || newQty;
            if (newQty > maxQty) newQty = maxQty;
            return Object.assign({}, ci, { quantity: newQty, availableQuantity: item.availableQuantity });
          }
          return ci;
        });
        localStorage.setItem('primal_cart', JSON.stringify(newCart));
        return { cart: newCart };
      }, function() {
        window.dispatchEvent(new Event('cartUpdated'));
      });
      return;
    }
    this.setState(function(prev) {
      var newCart = prev.cart.concat([Object.assign({}, item, { quantity: qty, addedAt: Date.now() })]);
      localStorage.setItem('primal_cart', JSON.stringify(newCart));
      return { cart: newCart };
    }, function() {
      window.dispatchEvent(new Event('cartUpdated'));
    });
  }

  removeFromCart(postId) {
    this.setState(function(prev) {
      var newCart = prev.cart.filter(function(item) { return item.postId !== postId; });
      localStorage.setItem('primal_cart', JSON.stringify(newCart));
      return { cart: newCart };
    });
  }

  updateCartQuantity(postId, qty, availableQuantity) {
    if (qty < 1) return;
    this.setState(function(prev) {
      var newCart = prev.cart.map(function(item) {
        if (item.postId === postId) {
          var updates = { quantity: qty };
          if (availableQuantity !== undefined) updates.availableQuantity = availableQuantity;
          return Object.assign({}, item, updates);
        }
        return item;
      });
      localStorage.setItem('primal_cart', JSON.stringify(newCart));
      return { cart: newCart };
    });
  }

  updateCartItem(postId, updates) {
    this.setState(function(prev) {
      var newCart = prev.cart.map(function(item) {
        if (item.postId === postId) {
          return Object.assign({}, item, updates);
        }
        return item;
      });
      localStorage.setItem('primal_cart', JSON.stringify(newCart));
      return { cart: newCart };
    });
  }

  replaceCart(newCart) {
    var cart = Array.isArray(newCart) ? newCart : [];
    localStorage.setItem('primal_cart', JSON.stringify(cart));
    this.setState({ cart: cart });
  }

  clearCart() {
    this.setState({ cart: [] });
    localStorage.removeItem('primal_cart');
  }

  isInCart(postId) {
    return this.state.cart.some(function(item) { return item.postId === postId; });
  }

  render() {
    var cart = this.state.cart;
    var cartCount = 0;
    cart.forEach(function(item) { cartCount += (item.quantity || 1); });
    return (
      <CartContext.Provider
        value={{
          cart: cart,
          cartCount: cartCount,
          addToCart: this.addToCart,
          removeFromCart: this.removeFromCart,
          updateCartQuantity: this.updateCartQuantity,
          updateCartItem: this.updateCartItem,
          replaceCart: this.replaceCart,
          clearCart: this.clearCart,
          isInCart: this.isInCart,
        }}
      >
        {this.props.children}
      </CartContext.Provider>
    );
  }
}

export { CartProvider };
export default CartContext;
